import { EventBus } from '@/plugins/event-bus'
import ChangeOrder from 'api-client/ChangeOrder'
import Customers from 'api-client/Customers'
import { isEqual } from 'lodash'
import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'

const moduleState = {
  changeOrders: [],
  changeOrder: {
    ChangeOrderId: '',
    BrandCode: '',
    DealerId: '',
    KomNumber: '',
    KomYear: '',
    FactoryStatusNumber: '',
    FactoryStatusDescription: '',
    IsCanDeleteOrder: null,
    MainOrderStatus: '',
    ChangeOrderStatus: '',
    CreateDate: '',
    ChangeDate: '',
    Employee: null,
    OwningDealer: {
      OriginalBrand: '',
      OriginalCountry: '',
      OriginalPartnerNumber: '',
      ChangedBrand: '',
      ChangedCountry: '',
      ChangedPartnerNumber: '',
      OrderStatus: '',
    },
    DeliveryDate: {
      OriginalDeliverAsSoonAsPossible: '',
      OriginalNotDeliverBeforeDate: '',
      ChangedDeliverAsSoonAsPossible: '',
      ChangedNotDeliverBeforeDate: '',
      OrderStatus: '',
    },
    AdditionalInfo: {
      OriginalAdditionalInfo: '',
      ChangedAdditionalInfo: '',
      OrderStatus: '',
    },
    UnloadingLocation: {
      OriginalUnloadingLocationId: '',
      OriginalDeliveredToDealerNumber: '',
      ChangedUnloadingLocationId: '',
      ChangedDeliveredToDealerNumber: '',
      OrderStatus: '',
    },
    Customer: {
      OriginalCustomerId: '',
      OriginalCustomerName: '',
      OriginalDriverId: '',
      OriginalLesseeId: '',
      OriginalCustomerAssignmentDate: '',
      ChangedCustomerId: '',
      ChangedCustomerName: '',
      ChangedDriverId: '',
      ChangedLesseeId: '',
      OrderStatus: '',
    },
    LicensePlates: {
      LicensePlateNumberRequested: false,
      OriginalLicensePlateType: null,
      OriginalQuantityWhiteLicensePlates: null,
      ChangedLicensePlateType: null,
      ChangedQuantityWhiteLicensePlates: null,
      OrderStatus: '',
    },
    TaxFree: {
      OriginalTaxFreeRequest: null,
      ChangedTaxFreeRequest: null,
      OrderStatus: '',
    },
    Reservation: {
      OriginalIsReserved: null,
      OriginalReservationStart: null,
      OriginalReservationEnd: null,
      OriginalReservingDealerNumber: null,
      ChangedIsReserved: null,
      ChangedReservingDealerNumber: null,
      OrderStatus: '',
    },
    Holds: {
      CommercialHold: {
        OriginalCommercialHold: null,
        OriginalCommercialHoldExpiryDate: null,
        ChangedCommercialHold: null,
        ChangedCommercialHoldExpiryDate: null,
        OrderStatus: '',
      },
      ShowroomHold: {
        OriginalShowroomHold: null,
        OriginalShowroomHoldExpiryDate: null,
        ChangedShowroomHold: null,
        ChangedShowroomHoldExpiryDate: null,
        OrderStatus: '',
      },
      StockHold: {
        OsmLocatingFlagSet: null,
        OsmBlockingReasonSet: null,
        OriginalStockHold: null,
        OriginalStockHoldExpiryDate: null,
        ChangedStockHold: null,
        ChangedStockHoldExpiryDate: null,
        OrderStatus: '',
      },
      TransportHold: {
        OriginalTransportHold: null,
        OriginalTransportHoldExpiryDate: null,
        ChangedTransportHold: null,
        ChangedTransportHoldExpiryDate: null,
        OrderStatus: '',
      },
    },
    CarConfiguration: {
      LicensePlateNumber: '',
      OrderCode: '',
      OkapiTypeCode: '',
      OkapiModelCode: '',
      ModelName: '',
      TrimlineName: '',
      CatalogCarName: '',
      FuelTypes: '',
      WltpCO2Value: '',
      WltpCO2Unit: '',
      EngineType: '',
      EngineDescription: '',
      Consumption: '',
      ConsumptionUnit: '',
      Range: '',
      RangeUnit: '',
      AllElectricRange: '',
      AllElectricRangeUnit: '',
      CocaId: '',
      ModelYear: 1900,
      BpmCode: null,
      ValidStatus: null,
      Images: null,
      OriginalPriceReferenceDate: '',
      ChangedPriceReferenceDate: '',
      Remark: '',
      OrderStatus: '',
      PendingDealerRequest: null,
      PhysicalLocation: null,
      RegistrationDate: null,
      ReservationFlag: null,
      ReservationStart: null,
      ReservationEnd: null,
      OriginalOptions: [],
      OriginalWltpValues: {
        WltpCO2Emission: null,
        WltpFuelConsumption: null,
        WltpRange: null,
        HasFailedToFetch: null,
      },
      OriginalBpm: null,
      OriginalPrice: null,
      OriginalNetCatalogPrice: null,
      ChangedOptions: [],
      ChangedWltpValues: {
        WltpCO2Emission: null,
        WltpFuelConsumption: null,
        WltpRange: null,
        HasFailedToFetch: null,
      },
      ChangedBpm: null,
      ChangedPrice: null,
      ChangedNetCatalogPrice: null,
      AvailableOptions: [],
      HasUnknownOkapiModel: null,
    },
    Permissions: {
      CanChangeColor: {
        Allowed: null,
        Reason: null,
      },
      CanChangeInterior: {
        Allowed: null,
        Reason: null,
      },
      CanChangeFactoryOption: {
        Allowed: null,
        Reason: null,
      },
      CanChangeImporterOptions: {
        Allowed: null,
        Reason: null,
      },
      CanChangeOwningDealer: {
        Allowed: null,
        Reason: null,
      },
      CanChangeRemark: {
        Allowed: null,
        Reason: null,
      },
      CanChangeUnloadingLocation: {
        Allowed: null,
        Reason: null,
      },
      CanChangeCustomer: {
        Allowed: null,
        Reason: null,
      },
      CanDeleteOrder: {
        Allowed: null,
        Reason: null,
      },
      CanChangeSettlement: {
        Allowed: null,
        Reason: null,
      },
      CanChangeLicensePlate: {
        Allowed: null,
        Reason: null,
      },
      CanChangeReservations: {
        Allowed: null,
        Reason: null,
      },
      CanChangeDeliveryDates: {
        Allowed: null,
        Reason: null,
      },
      CanChangeHoldCommercial: {
        Allowed: null,
        Reason: null,
      },
      CanChangeHoldShowroom: {
        Allowed: null,
        Reason: null,
      },
      CanChangeHoldStock: {
        Allowed: null,
        Reason: null,
      },
      CanChangeHoldTransport: {
        Allowed: null,
        Reason: null,
      },
    },
    ValidationMessages: [],
    DigitalSigningRequested: false,
    SigningStatus: '',
  },
  remarkChanged: false,
}

const getters = {
  unloadingLocations: (state) => state.unloadingLocations,
  establishments: (state) => state.establishments,
  all: (state) => state.changeOrders,
  get: (state) => state.changeOrder,
  changeOrderStatusIsConcept: (state) =>
    !state?.changeOrder?.MainOrderStatus || state?.changeOrder?.MainOrderStatus === 'CONCEPT',
  changeOrderSigningStatusIsEmpty: (state) =>
    !state?.changeOrder?.SigningStatus ||
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.failedToSend),
  changeOrderSigningStatusIsPending: (state) =>
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.sentToCustomer) ||
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.openedByCustomer) ||
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.manualSignPending),
  changeOrderSigningStatusIsCompleted: (state) =>
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.signCompleted) ||
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.signedByCustomer) ||
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.documentSignedByCustomer) ||
    state?.changeOrder?.SigningStatus?.endsWith(digitalSigningStatus.manualSignCompleted),
  changeOrderStatusIsSubmitted: (state) => state?.changeOrder?.MainOrderStatus === 'SUBMITTED',
}

const mutations = {
  setCustomerCompany: (state, payload) => {
    let customerCompanyPoId = payload?.CustomerCompanyPoId
    let customerCompanyMagicNumber = payload?.CustomerCompanyMagicNumber

    state.changeOrder.Customer.ChangedCustomerName = payload?.CustomerCompanyName

    if (!customerCompanyPoId) {
      state.changeOrder.Customer.ChangedCustomerId = customerCompanyMagicNumber
    } else {
      state.changeOrder.Customer.ChangedCustomerId = customerCompanyPoId
    }
  },
  setUnloadingLocations: (state, payload) => {
    state.unloadingLocations = payload
    EventBus.$emit('changeOrder/unloadingLocations')
  },
  setEstablishments: (state, payload) => {
    state.establishments = payload
    EventBus.$emit('changeOrder/establishments')
  },
  setAdditionalInfoStatus: (state) => {
    if (
      state.changeOrder.AdditionalInfo.OriginalAdditionalInfo !==
      state.changeOrder.AdditionalInfo.ChangedAdditionalInfo
    ) {
      state.changeOrder.AdditionalInfo.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.AdditionalInfo.OrderStatus = 'ORIGINAL'
    }
  },
  getUnloadingLocations: ({ commit }) => {
    ChangeOrder.getUnloadingLocations().then((response) => {
      commit('setUnloadingLocations', response.data || [])
    })
  },
  getEstablishments: ({ commit }) => {
    ChangeOrder.getEstablishments().then((response) => {
      commit('setEstablishments', response.data || [])
    })
  },
  setChangeOrders: (state, payload) => {
    state.changeOrders = payload
    EventBus.$emit('changeOrder/all')
  },
  setChangeOrder: (state, payload) => {
    state.changeOrder = payload
  },
  resetChangeOrder: (state) => {
    state.changeOrder = null
  },
  setRemarkChanged: (state) => {
    state.remarkChanged = true
  },
  setCustomerStatus: (state) => {
    if (
      state.changeOrder.Customer.OriginalCustomerId !== state.changeOrder.Customer.ChangedCustomerId
    ) {
      state.changeOrder.Customer.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.Customer.OrderStatus = 'ORIGINAL'
    }
  },
  setOwningDealerStatus: (state) => {
    let changedOwningDealer =
      state.changeOrder.OwningDealer.ChangedPartnerNumber !==
      state.changeOrder.OwningDealer.OriginalPartnerNumber

    if (changedOwningDealer) {
      state.changeOrder.OwningDealer.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.OwningDealer.OrderStatus = 'ORIGINAL'
    }
  },
  setUnloadingLocationStatus: (state) => {
    let changedUnloadingLocationId =
      state.changeOrder.UnloadingLocation.ChangedUnloadingLocationId !==
      state.changeOrder.UnloadingLocation.OriginalUnloadingLocationId

    if (changedUnloadingLocationId) {
      state.changeOrder.UnloadingLocation.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.UnloadingLocation.OrderStatus = 'ORIGINAL'
    }
  },
  setDeliveryDateStatus: (state) => {
    let changedDeliverAsSoonAsPossible =
      state.changeOrder.DeliveryDate.ChangedDeliverAsSoonAsPossible !==
      state.changeOrder.DeliveryDate.OriginalDeliverAsSoonAsPossible

    let changedNotDeliverBeforeDate =
      state.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate !==
      state.changeOrder.DeliveryDate.OriginalNotDeliverBeforeDate

    if (changedDeliverAsSoonAsPossible || changedNotDeliverBeforeDate) {
      state.changeOrder.DeliveryDate.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.DeliveryDate.OrderStatus = 'ORIGINAL'
    }
  },
  setLicensePlatesStatus: (state) => {
    //Set commercial hold status

    let changedCommercialHold =
      state.changeOrder.Holds.CommercialHold.ChangedCommercialHold !==
      state.changeOrder.Holds.CommercialHold.OriginalCommercialHold

    let changedCommercialHoldExpiryDate =
      state.changeOrder.Holds.CommercialHold.ChangedCommercialHoldExpiryDate !==
      state.changeOrder.Holds.CommercialHold.OriginalCommercialHoldExpiryDate

    if (changedCommercialHold || changedCommercialHoldExpiryDate) {
      state.changeOrder.Holds.CommercialHold.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.Holds.CommercialHold.OrderStatus = 'ORIGINAL'
    }

    let changedLicensePlateType =
      state.changeOrder.LicensePlates.ChangedLicensePlateType !==
      state.changeOrder.LicensePlates.OriginalLicensePlateType

    let changedQuantityWhiteLicensePlates =
      state.changeOrder.LicensePlates.ChangedQuantityWhiteLicensePlates !==
      state.changeOrder.LicensePlates.OriginalQuantityWhiteLicensePlates

    let LicensePlateNumberRequested = state.changeOrder.LicensePlates.LicensePlateNumberRequested

    if (
      changedLicensePlateType ||
      changedQuantityWhiteLicensePlates ||
      LicensePlateNumberRequested
    ) {
      state.changeOrder.LicensePlates.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.LicensePlates.OrderStatus = 'ORIGINAL'
    }

    if (
      state.changeOrder.TaxFree.ChangedTaxFreeRequest !==
      state.changeOrder.TaxFree.OriginalTaxFreeRequest
    ) {
      state.changeOrder.TaxFree.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.TaxFree.OrderStatus = 'ORIGINAL'
    }
  },
  setTransportHoldStatus: (state) => {
    //Set transport hold status

    let changedTransportHold =
      state.changeOrder.Holds.TransportHold?.ChangedTransportHold !==
      state.changeOrder.Holds.TransportHold?.OriginalTransportHold

    let changedTransportHoldExpiryDate =
      state.changeOrder.Holds.TransportHold?.ChangedTransportHoldExpiryDate !==
      state.changeOrder.Holds.TransportHold?.OriginalTransportHoldExpiryDate

    if (changedTransportHold || changedTransportHoldExpiryDate) {
      state.changeOrder.Holds.TransportHold.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.Holds.TransportHold.OrderStatus = 'ORIGINAL'
    }
  },
  setReservationStatus: (state) => {
    let changedReservingDealerNumber =
      state.changeOrder.Reservation.ChangedReservingDealerNumber !==
      state.changeOrder.Reservation.OriginalReservingDealerNumber

    let changedIsReserved =
      state.changeOrder.Reservation.ChangedIsReserved !==
      state.changeOrder.Reservation.OriginalIsReserved

    if (changedReservingDealerNumber || changedIsReserved) {
      state.changeOrder.Reservation.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.Reservation.OrderStatus = 'ORIGINAL'
    }

    //Set showroom hold status

    let changedShowroomHold =
      state.changeOrder.Holds.ShowroomHold.ChangedShowroomHold !==
      state.changeOrder.Holds.ShowroomHold.OriginalShowroomHold

    let changedShowroomHoldExpiryDate =
      state.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate !==
      state.changeOrder.Holds.ShowroomHold.OriginalShowroomHoldExpiryDate

    if (changedShowroomHold || changedShowroomHoldExpiryDate) {
      state.changeOrder.Holds.ShowroomHold.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.Holds.ShowroomHold.OrderStatus = 'ORIGINAL'
    }

    //Set stock hold status

    let changedStockHold =
      state.changeOrder.Holds.StockHold.ChangedStockHold !==
      state.changeOrder.Holds.StockHold.OriginalStockHold

    let changedStockHoldExpiryDate =
      state.changeOrder.Holds.StockHold.ChangedStockHoldExpiryDate !==
      state.changeOrder.Holds.StockHold.OriginalStockHoldExpiryDate

    if (changedStockHold || changedStockHoldExpiryDate) {
      state.changeOrder.Holds.StockHold.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.Holds.StockHold.OrderStatus = 'ORIGINAL'
    }
  },
  setCarConfigurationStatus: (state) => {
    let changedShortIds = state.changeOrder.CarConfiguration.ChangedOptions.map(
      (option) => option.ShortId
    ).sort()

    let originalShortIds = state.changeOrder.CarConfiguration.OriginalOptions.map(
      (option) => option.ShortId
    ).sort()

    if (!isEqual(originalShortIds, changedShortIds)) {
      state.changeOrder.CarConfiguration.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.CarConfiguration.OrderStatus = 'ORIGINAL'
    }
  },
  updateCarConfigurationChangedOptions: (state, options) => {
    state.changeOrder.CarConfiguration.ChangedOptions = options

    // Automatically update the order status
    let changedShortIds = options.map((option) => option.ShortId).sort()
    let originalShortIds = state.changeOrder.CarConfiguration.OriginalOptions.map(
      (option) => option.ShortId
    ).sort()

    if (!isEqual(originalShortIds, changedShortIds)) {
      state.changeOrder.CarConfiguration.OrderStatus = 'CHANGED'
    } else {
      state.changeOrder.CarConfiguration.OrderStatus = 'ORIGINAL'
    }
  },
}

const actions = {
  setCustomerCompany: async ({ commit, state }, payload) => {
    const customerCompany = payload ? payload : null

    commit('setCustomerCompany', customerCompany)

    // If we have a change order ID and are adding a company, use the new API
    const changeOrderId = state.changeOrder.ChangeOrderId
    if (changeOrderId && customerCompany && customerCompany.CustomerCompanyMagicNumber) {
      try {
        await Customers.addCustomerCompany(
          changeOrderId,
          customerCompany.CustomerCompanyMagicNumber,
          customerCompany.CustomerCompanySource
        )
      } catch (error) {
        console.error('Failed to add customer company to change order:', error)
        // Revert the commit if API call fails
        commit('setCustomerCompany', null)
        throw error
      }
    }
  },
  getUnloadingLocations: ({ commit }) => {
    ChangeOrder.getUnloadingLocations().then((response) => {
      commit('setUnloadingLocations', response.data || [])
    })
  },
  getEstablishments: ({ commit }) => {
    ChangeOrder.getEstablishments().then((response) => {
      commit('setEstablishments', response.data || [])
    })
  },
  addChangeOrders: ({ commit, state }, payload) => {
    let concatted = state.changeOrders.concat(payload)
    commit('setChangeOrders', concatted)
  },
  resetChangeOrders: ({ commit }) => {
    commit('setChangeOrders', [])
  },
  setChangeOrder: ({ commit }, payload) => {
    commit('setChangeOrder', payload)
  },
  clearChangeOrder: ({ commit }) => {
    commit('resetChangeOrder')
  },
}
export default {
  state: moduleState,
  getters,
  mutations,
  actions,
  namespaced: true,
}
